import React from 'react';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { DefaultFormData, FormPickerDatas } from '../../local-types';
import { withTranslation, WithTranslation } from 'react-i18next';
import StringUtils from '../../utils/stringUtils';

interface FormOptionsProps extends DefaultFormData, WithTranslation {
    value: string;
    noEmpty: boolean;
    searchable?: boolean;
    multipleOptions: boolean;
    options: FormPickerDatas[];
    optionsGroups: JSX.Element;
}

// Keep only the options matching every word typed, whatever the case or the accents
const filterOptions = (options: FormPickerDatas[], { inputValue }: { inputValue: string }): FormPickerDatas[] => {
    const words = StringUtils.normalizeForSearch(inputValue).split(/\s+/).filter(Boolean);
    if (words.length === 0) {
        return options;
    }
    return options.filter((option) => {
        const candidate = StringUtils.normalizeForSearch(option.name);
        return words.every((word) => candidate.includes(word));
    });
};

const FormOptions = (props: FormOptionsProps): JSX.Element => {
    const classes = useStyles();
    const {
        t,
        value,
        options,
        optionsGroups,
        noEmpty,
        searchable,
        multipleOptions,
        disabled,
        required,
        hint,
        placeholder,
        id,
        name,
        label,
        style,
        handleChange,
    } = props;

    if (searchable && !optionsGroups) {
        const selected: FormPickerDatas = options?.find((o) => String(o.id) === String(value)) ?? null;

        return (
            <FormControl>
                <div className={classes.formLabel}>{label}</div>
                <Autocomplete
                    id={id}
                    options={options ?? []}
                    value={selected}
                    className={classes.formInput}
                    style={style}
                    disabled={disabled}
                    disableClearable={noEmpty || !multipleOptions}
                    filterOptions={filterOptions}
                    getOptionLabel={(option: FormPickerDatas) => option?.name ?? ''}
                    getOptionSelected={(option: FormPickerDatas, val: FormPickerDatas) => option?.id === val?.id}
                    noOptionsText={t('common:noValue')}
                    onChange={(event, option: FormPickerDatas) =>
                        handleChange({ target: { id, name, value: option?.id ?? '' } })
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            color="secondary"
                            className={classes.autocompleteInput}
                            placeholder={placeholder}
                            required={required}
                        />
                    )}
                />
                {/* Keeps the id (and not the label) in the native form datas, like the select below */}
                <input type="hidden" name={name} value={value ?? ''} />
                {hint && <div className={classes.hint}>{hint}</div>}
            </FormControl>
        );
    }

    return (
        <FormControl>
            <div className={classes.formLabel}>{label}</div>
            <Select
                native
                id={id}
                name={name}
                placeholder={placeholder}
                value={value ?? ''}
                className={classes.formInput}
                style={style}
                variant="outlined"
                required={required}
                disabled={disabled}
                onChange={handleChange}
            >
                {!noEmpty && multipleOptions && (
                    <option key="empty" value="">
                        {''}
                    </option>
                )}
                {optionsGroups ||
                    (options.length > 0 ? (
                        options.map((select) => (
                            <option key={select.id} value={select.id}>
                                {select.name}
                            </option>
                        ))
                    ) : (
                        <option key="empty" value="">
                            {t('common:noValue')}
                        </option>
                    ))}
            </Select>
            {hint && <div className={classes.hint}>{hint}</div>}
        </FormControl>
    );
};

//=====================================//
//=============== Style ===============//
//=====================================//

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        formLabel: {
            fontSize: 12,
            color: theme.palette.primary.dark,
            fontWeight: 500,
        },
        formInput: {
            width: '100%',
            minWidth: 200,
            marginTop: 7,
        },
        // The text field of the autocomplete is a FormControl too, it must not take the form spacing again
        autocompleteInput: {
            margin: 0 + 'px!important',
        },
        hint: {
            fontSize: 12,
            color: theme.palette.text.secondary,
        },
    }),
);

export default withTranslation(['common'])(FormOptions);
